<template>
    <div class="global-env animated fadeIn">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <b-skeleton width="40%" height="28px" animation="throb" class="mb-2" />
            <b-skeleton width="60%" height="16px" animation="throb" class="mb-4" />
            <b-skeleton width="100%" height="300px" animation="throb" />
        </div>

        <div v-else>
            <!-- Page Header -->
            <div class="page-header">
                <div class="header-left">
                    <h4 class="page-title">{{ $t('globalenvironment') }}</h4>
                    <p class="page-description">{{ $t('globalEnvironmentDesc') }}</p>
                </div>
                <div class="header-actions">
                    <div class="env-toggle">
                        <button 
                            class="env-btn" 
                            :class="{ 'active': !previewEnv }" 
                            @click="previewEnv = false"
                        >{{ $t('globalEnvDev') }}</button>
                        <button 
                            class="env-btn" 
                            :class="{ 'active': previewEnv, 'live': previewEnv }" 
                            @click="previewEnv = true"
                        >{{ $t('globalEnvLive') }}</button>
                    </div>
                </div>
            </div>

            <!-- View Mode Tabs -->
            <div class="view-tabs">
                <button 
                    class="view-tab" 
                    :class="{ 'active': viewMode === 'visual' }" 
                    @click="viewMode = 'visual'"
                >
                    <i class="icon-grid"></i>
                    {{ $t('globalEnvVisualEditor') }}
                </button>
                <button 
                    class="view-tab" 
                    :class="{ 'active': viewMode === 'json' }" 
                    @click="viewMode = 'json'"
                >
                    <i class="icon-doc"></i>
                    {{ $t('globalEnvJsonEditor') }}
                </button>
            </div>

            <!-- Visual Editor -->
            <div v-if="viewMode === 'visual'" class="variables-card">
                <!-- Search & Count Bar -->
                <div class="variables-toolbar">
                    <div class="search-box">
                        <i class="icon-magnifier search-icon"></i>
                        <input 
                            type="text" 
                            class="search-input" 
                            :placeholder="$t('globalEnvSearchPlaceholder')" 
                            v-model="searchQuery"
                        />
                        <span v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
                            <i class="icon-close"></i>
                        </span>
                    </div>
                    <div class="variables-count">
                        <span class="count-badge">{{ filteredVariables.length }}</span>
                        {{ $t('globalEnvVariablesCount') }}
                    </div>
                </div>

                <!-- Table Header -->
                <div class="table-header">
                    <div class="col-key">{{ $t('globalEnvKey') }}</div>
                    <div class="col-type">{{ $t('globalEnvType') }}</div>
                    <div class="col-value">{{ $t('globalEnvValue') }}</div>
                    <div class="col-actions"></div>
                </div>

                <!-- Empty State -->
                <div v-if="filteredVariables.length === 0 && searchQuery" class="empty-state">
                    <i class="icon-magnifier empty-icon"></i>
                    <p>{{ $t('globalEnvNoResults') }}</p>
                </div>

                <!-- Variables List -->
                <div class="variables-list">
                    <div 
                        v-for="(variable, index) in filteredVariables" 
                        :key="variable._id || index"
                        class="variable-row"
                        :class="{ 'has-error': variable.isDuplicate, 'is-new': !variable.key }"
                    >
                        <!-- Key -->
                        <div class="col-key">
                            <input 
                                class="field-input" 
                                type="text"
                                :placeholder="$t('globalEnvKeyPlaceholder')"
                                v-model.trim.lazy="variable.key"
                                :class="{ 'input-error': variable.isDuplicate }"
                            />
                            <transition name="fade">
                                <span class="error-text" v-if="variable.isDuplicate">
                                    <i class="icon-exclamation"></i>
                                    {{ $t('globalEnvDuplicateKey') }}
                                </span>
                            </transition>
                        </div>

                        <!-- Type -->
                        <div class="col-type">
                            <div class="type-select-wrapper">
                                <select 
                                    class="field-select" 
                                    v-model="variable.type"
                                    @change="(event) => handleTypeChanges(event, getOriginalIndex(variable))"
                                >
                                    <option value="String">String</option>
                                    <option value="Number">Number</option>
                                    <option value="Boolean">Boolean</option>
                                    <option value="Array">Array</option>
                                </select>
                                <i class="icon-arrow-down select-chevron"></i>
                            </div>
                        </div>

                        <!-- Value -->
                        <div class="col-value">
                            <v-select 
                                v-if="variable.type === 'Array'" 
                                class="array-select" 
                                v-model="variable.value" 
                                multiple 
                                taggable 
                                noDrop
                                :placeholder="$t('globalEnvArrayPlaceholder')"
                            />

                            <input 
                                v-else-if="variable.type === 'Number'" 
                                type="number" 
                                class="field-input" 
                                :placeholder="$t('globalEnvNumberPlaceholder')"
                                v-model.number="variable.value"
                            />

                            <div v-else-if="variable.type === 'Boolean'" class="boolean-toggle">
                                <c-switch 
                                    color="primary" 
                                    variant="pill" 
                                    :label="false"
                                    v-model="variable.value" 
                                />
                                <span class="boolean-label">{{ variable.value ? 'true' : 'false' }}</span>
                            </div>

                            <input 
                                v-else 
                                type="text" 
                                class="field-input" 
                                :placeholder="$t('globalEnvStringPlaceholder')"
                                v-model.trim="variable.value"
                            />
                        </div>

                        <!-- Actions -->
                        <div class="col-actions">
                            <button 
                                class="action-btn delete-btn" 
                                @click="removeVariable(getOriginalIndex(variable))"
                                :disabled="variables.length <= 1"
                                :title="$t('delete')"
                            >
                                <i class="icon-trash"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Add Variable Button -->
                <button class="add-variable-btn" @click="addNewVariable">
                    <i class="icon-plus"></i>
                    {{ $t('globalEnvAddVariable') }}
                </button>
            </div>

            <!-- JSON Editor -->
            <div v-else class="json-card">
                <CodeEditor lang="application/json" :controls="false" v-model.lazy="editor" :withButton="false" />
            </div>

            <!-- Footer Actions -->
            <div class="footer-actions">
                <b-button variant="primary" class="save-btn" @click="save">
                    <i class="icon-check mr-1"></i>
                    {{ $t('save') }}
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
import { Switch as cSwitch } from '@coreui/vue';
import CodeEditor from '@/components/form/Editor/Code';
import VSelect from 'vue-select'

let nextId = 1;

export default {
    components: {
        cSwitch,
        CodeEditor,
        VSelect
    },
    data() {
        return {
            viewMode: 'visual',
            variables: [{ _id: nextId++, key: '', value: '', type: 'String', isDuplicate: false }],
            editor: '{}',
            loading: true,
            previewEnv: false,
            searchQuery: '',
            _syncingFromVariables: false,
            _syncingFromEditor: false,
        };
    },
    computed: {
        filteredVariables() {
            if (!this.searchQuery) return this.variables;
            const q = this.searchQuery.toLowerCase();
            return this.variables.filter(v => 
                v.key.toLowerCase().includes(q) || 
                String(v.value).toLowerCase().includes(q)
            );
        }
    },
    watch: {
        variables: {
            deep: true,
            handler(newType) {
                if (this._syncingFromEditor) return;
                const seenKeys = new Set();

                newType.forEach((variable, index) => {
                    const key = variable.key;
                    if (key && seenKeys.has(key)) {
                        this.$set(this.variables[index], 'isDuplicate', true);
                    } else {
                        this.$set(this.variables[index], 'isDuplicate', false);
                        if (key) seenKeys.add(key);
                    }
                });

                this._syncingFromVariables = true;
                let data = JSON.stringify(newType)
                let parseData = JSON.parse(data)
                this.editor = JSON.stringify(this.convertArray(parseData), null, 4)
                this.$nextTick(() => { this._syncingFromVariables = false; });
            },
        },
        editor: {
            deep: true,
            handler(newVal) {
                if (this._syncingFromVariables) return;
                try {
                    let parsedData = JSON.parse(newVal);
                    let flattedOBJ = this.flattenObject(parsedData)
                    let data = Object.entries(flattedOBJ).map(([key, value]) => {
                        return { _id: nextId++, key, value, "type": this.determineType(value), isDuplicate: false };
                    });

                    this._syncingFromEditor = true;
                    this.variables = data
                    this.$nextTick(() => { this._syncingFromEditor = false; });
                } catch (error) {
                    console.error('Error parsing JSON:', error);
                }
            },
        },

        previewEnv() {
            this.getEnv()
        }
    },
    methods: {
        getOriginalIndex(variable) {
            return this.variables.indexOf(variable);
        },
        handleTypeChanges(event, index) {
            if (index === -1) return;
            this.$set(this.variables[index], 'value', "");
        },
        addNewVariable() {
            this.variables.push({ _id: nextId++, key: '', value: '', type: 'String', isDuplicate: false });
        },
        removeVariable(index) {
            if (index === -1) return;
            if (this.variables.length > 1) {
                this.variables.splice(index, 1);
            }
        },
        convertArray(array) {
            const result = {};

            array.map(item => {
                if (item.isDuplicate) {
                    return
                }
                else if (item.key && item.key.includes('.')) {
                    const keys = item.key.split('.');
                    let current = result;

                    keys.forEach((key, index) => {
                        if (index === keys.length - 1) {
                            current[key] = item.value;
                        } else {
                            if (!current[key]) {
                                current[key] = {};
                            }
                            current = current[key];
                        }
                    });
                } else {
                    result[item.key] = item.value;
                }
            });

            return result;
        },

        flattenObject(obj, prefix = '') {
            let result = {};
            for (const key in obj) {
                if (!obj.hasOwnProperty(key)) {
                    continue;
                }
                const newKey = prefix ? `${prefix}.${key}` : key;
                const value = obj[key];
                if (Array.isArray(value)) {
                    result[newKey] = value;
                } else if (typeof value === 'object' && value !== null) {
                    Object.assign(result, this.flattenObject(value, newKey));
                } else {
                    result[newKey] = value;
                }
            }
            return result;
        },

        determineType(value) {
            const type = typeof value;

            if (type === 'string') {
                return 'String';
            } else if (type === 'number') {
                return 'Number';
            } else if (type === 'boolean') {
                return 'Boolean';
            }

            if (Array.isArray(value)) {
                return 'Array';
            }
            return 'String';
        },

        async save() {
            const popup = this.$loadingPopup()

            try {
                await this.$http.post("/v1/system/platform/env", JSON.parse(this.editor), {
                    headers: {
                        'X-Project-Env': this.previewEnv ? 'live' : 'dev'
                    }
                })
            } catch (err) {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger'
                })

                console.error(err)
            } finally {
                popup.hide()
            }
        },

        async getEnv() {
            try {
                const { data } = await this.$http.get("/v1/system/platform/env", {
                    headers: {
                        'X-Project-Env': this.previewEnv ? 'live' : 'dev'
                    }
                })

                this.editor = JSON.stringify(data)

                this.loading = false
            } catch (err) {
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger'
                })

                console.error(err)
            }
        }

    },

    async mounted() {
        this.getEnv()
    }
};
</script>


<style scoped>
/* Page Layout */
.global-env {
    max-width: 960px;
    padding: var(--spacing-xl) var(--spacing-2xl);
}

.loading-state {
    padding: var(--spacing-2xl);
}

/* Page Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--spacing-2xl);
    flex-wrap: wrap;
    gap: var(--spacing-lg);
}

.header-left {
    flex: 1;
    min-width: 200px;
}

.page-title {
    color: var(--text-primary);
    font-weight: 600;
    margin: 0 0 var(--spacing-xs) 0;
    font-size: 1.4rem;
}

.page-description {
    color: var(--text-muted);
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
}

/* Environment Toggle */
.env-toggle {
    display: flex;
    align-items: center;
    background: var(--bg-card-secondary);
    padding: var(--spacing-xs);
    border-radius: var(--radius-md);
    border: 1px solid var(--border-default);
}

.env-btn {
    font-size: 0.8rem;
    color: var(--text-muted);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: var(--spacing-sm) var(--spacing-lg);
    border: none;
    background: transparent;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: var(--transition-quick);
}

.env-btn:hover {
    color: var(--text-secondary);
}

.env-btn.active {
    background: var(--bg-card);
    color: var(--primary-start);
    box-shadow: var(--shadow-sm);
}

.env-btn.active.live {
    color: var(--success-start);
}

/* View Tabs */
.view-tabs {
    display: flex;
    gap: var(--spacing-xs);
    margin-bottom: var(--spacing-xl);
    background: var(--bg-card-secondary);
    padding: var(--spacing-xs);
    border-radius: var(--radius-md);
    width: fit-content;
}

.view-tab {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-lg);
    border: none;
    background: transparent;
    color: var(--text-muted);
    font-size: 0.85rem;
    font-weight: 500;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: var(--transition-quick);
}

.view-tab:hover {
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.04);
}

.view-tab.active {
    background: var(--bg-card);
    color: var(--text-primary);
    box-shadow: var(--shadow-sm);
}

/* Variables Card */
.variables-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-xl);
    overflow: hidden;
}

/* Toolbar */
.variables-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-lg) var(--spacing-xl);
    border-bottom: 1px solid var(--border-default);
    gap: var(--spacing-lg);
    flex-wrap: wrap;
}

.search-box {
    position: relative;
    flex: 1;
    max-width: 320px;
    min-width: 180px;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    font-size: 0.85rem;
}

.search-input {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) 36px;
    background: var(--form-bg);
    border: 1px solid var(--form-border);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 0.85rem;
    transition: var(--transition-quick);
    outline: none;
}

.search-input:focus {
    border-color: var(--border-active);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
}

.search-input::placeholder {
    color: var(--text-disabled);
}

.search-clear {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: var(--text-muted);
    font-size: 0.75rem;
    transition: var(--transition-quick);
}

.search-clear:hover {
    color: var(--text-primary);
}

.variables-count {
    font-size: 0.8rem;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    white-space: nowrap;
}

.count-badge {
    background: rgba(102, 126, 234, 0.12);
    color: var(--primary-start);
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    font-weight: 600;
    font-size: 0.8rem;
}

/* Table Header */
.table-header {
    display: grid;
    grid-template-columns: 1fr 140px 1fr 50px;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-xl);
    background: var(--bg-card-secondary);
    border-bottom: 1px solid var(--border-default);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-4xl) var(--spacing-xl);
    color: var(--text-muted);
}

.empty-icon {
    font-size: 2rem;
    margin-bottom: var(--spacing-md);
    opacity: 0.4;
}

.empty-state p {
    font-size: 0.9rem;
    margin: 0;
}

/* Variables List */
.variables-list {
    position: relative;
}

.variable-row {
    display: grid;
    grid-template-columns: 1fr 140px 1fr 50px;
    gap: var(--spacing-md);
    padding: var(--spacing-md) var(--spacing-xl);
    align-items: start;
    border-bottom: 1px solid var(--border-separator);
    transition: var(--transition-quick);
}

.variable-row:hover {
    background: rgba(255, 255, 255, 0.02);
}

.variable-row.has-error {
    background: rgba(252, 129, 129, 0.04);
}

.variable-row:last-child {
    border-bottom: none;
}

/* Field Inputs */
.field-input {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md);
    background: var(--form-bg);
    border: 1px solid var(--form-border);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 0.85rem;
    transition: var(--transition-quick);
    outline: none;
    height: 36px;
}

.field-input:focus {
    border-color: var(--border-active);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
}

.field-input::placeholder {
    color: var(--text-disabled);
}

.field-input.input-error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 2px rgba(252, 129, 129, 0.15);
}

/* Type Select */
.type-select-wrapper {
    position: relative;
}

.field-select {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-2xl) var(--spacing-sm) var(--spacing-md);
    background: var(--form-bg);
    border: 1px solid var(--form-border);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 0.85rem;
    appearance: none;
    cursor: pointer;
    transition: var(--transition-quick);
    outline: none;
    height: 36px;
}

.field-select:focus {
    border-color: var(--border-active);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.15);
}

.select-chevron {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    font-size: 0.65rem;
    pointer-events: none;
}

/* Boolean Toggle */
.boolean-toggle {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    height: 36px;
}

.boolean-label {
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-family: monospace;
}

/* Array Select */
.array-select {
    font-size: 0.85rem;
}

/* Error Text */
.error-text {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: 0.75rem;
    color: var(--color-error);
    margin-top: var(--spacing-xs);
}

/* Action Buttons */
.col-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 36px;
}

.action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: var(--transition-quick);
    background: transparent;
    color: var(--text-muted);
}

.action-btn:hover:not(:disabled) {
    background: rgba(252, 129, 129, 0.12);
    color: var(--color-error);
}

.action-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

/* Add Variable Button */
.add-variable-btn {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    width: 100%;
    padding: var(--spacing-md) var(--spacing-xl);
    border: none;
    background: transparent;
    color: var(--primary-start);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: var(--transition-quick);
    border-top: 1px solid var(--border-default);
}

.add-variable-btn:hover {
    background: rgba(102, 126, 234, 0.06);
}

.add-variable-btn i {
    font-size: 0.8rem;
}

/* JSON Card */
.json-card {
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-xl);
    overflow: hidden;
}

/* Footer Actions */
.footer-actions {
    display: flex;
    justify-content: flex-end;
    padding: var(--spacing-xl) 0;
}

.save-btn {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    padding: var(--spacing-sm) var(--spacing-2xl);
    font-weight: 500;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-primary);
    transition: var(--transition-quick);
}

.save-btn:hover {
    box-shadow: var(--shadow-primary-hover);
    transform: translateY(-1px);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .global-env {
        padding: var(--spacing-lg);
    }

    .page-header {
        flex-direction: column;
    }

    .table-header,
    .variable-row {
        grid-template-columns: 1fr;
        gap: var(--spacing-sm);
    }

    .table-header {
        display: none;
    }

    .variable-row {
        padding: var(--spacing-lg) var(--spacing-xl);
    }

    .col-actions {
        justify-content: flex-end;
    }
}
</style>
